import { useEffect, useRef, useState, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

/*
 * Content that arrives as you scroll to it.
 *
 * **This is the single biggest reason a marketing page feels alive rather than
 * printed.** A page where everything is simply *there* reads as a document; a
 * page where a section settles into place as you reach it reads as software
 * somebody is still looking after.
 *
 * **No animation library.** `IntersectionObserver` plus a CSS transition is
 * about forty lines and adds nothing to the bundle — and this is a marketing
 * page for a cleaning business in Cedar City, opened on a phone in a car park.
 * Framer Motion would be roughly 40kB gzipped to move things eight pixels.
 *
 * **`prefers-reduced-motion` is honoured by never starting**, not by animating
 * faster. Somebody with a vestibular disorder has asked the operating system to
 * stop things moving; a shorter transition is still movement. When it is set,
 * the content is visible from the first paint and the observer is never
 * created.
 */

/** Read once — this does not change mid-visit in any way worth re-rendering for. */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export interface RevealProps {
  children: ReactNode;
  className?: string;
  /**
   * Stagger, in milliseconds, for items in a row.
   *
   * Small on purpose. Sixty milliseconds between three cards reads as *one*
   * movement with a bit of life in it; three hundred reads as three separate
   * things happening and makes the page feel slow.
   */
  delay?: number;
  /** `<li>` inside a list, `<div>` otherwise. */
  as?: 'div' | 'li';
}

export function Reveal({ children, className, delay = 0, as = 'div' }: RevealProps) {
  const ref = useRef<HTMLElement | null>(null);

  /*
   * **Starts visible when there is nothing to animate**, decided at first
   * render rather than in an effect. Two cases:
   *
   *   - `prefers-reduced-motion` is set. Starting hidden and revealing on mount
   *     would flash blank content for one frame for exactly the people who
   *     asked for less movement.
   *   - No `IntersectionObserver` at all. Degrade to the plain page, never to
   *     an empty one — a reveal that can never fire is content nobody can read.
   *
   * Both are properties of the environment and neither changes while the page
   * is open, so they belong in the initial state and not in an effect that
   * would cause a second render to fix the first one.
   */
  const [shown, setShown] = useState(
    () => prefersReducedMotion() || typeof IntersectionObserver === 'undefined',
  );

  useEffect(() => {
    if (shown) return;

    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true);
            // **Once.** Re-hiding on scroll-up is the thing that makes these
            // pages feel like a toy rather than a business.
            observer.disconnect();
          }
        }
      },
      {
        /*
         * `-10%` on the bottom, so a section starts moving slightly *before*
         * it reaches the fold rather than after. Revealing at the exact edge
         * means the movement finishes below where the eye already is, and the
         * effect is lost.
         */
        rootMargin: '0px 0px -10% 0px',
        threshold: 0.05,
      },
    );

    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [shown]);

  const Component = as;

  return (
    <Component
      ref={ref as never}
      style={shown && delay > 0 ? { transitionDelay: `${String(delay)}ms` } : undefined}
      className={cn(
        'motion-safe:transition-[opacity,transform] motion-safe:duration-700 motion-safe:ease-out',
        // 16px, not 60. A big travel distance reads as a slideshow; a small one
        // reads as the page settling.
        shown ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0',
        className,
      )}
    >
      {children}
    </Component>
  );
}
